import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Form,
  FormButton,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import {
  SearchUserAppRoleViewDocument,
  useAssignUserAppRoleMutation,
} from '@user-management/graphql';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { RoleUserAdditionList } from '../role-user-addition-list';

enum AddUserToRoleErrorType {
  // NOTE: Assume that the error message CANNOT_ASSIGN_APPROLE_TO_SELF prohibits users from adding themselves
  TRYING_TO_ASSIGN_SELF = 'CANNOT_ASSIGN_APPROLE_TO_SELF',
}

const ERROR_MESSAGE: Record<AddUserToRoleErrorType, string> = {
  [AddUserToRoleErrorType.TRYING_TO_ASSIGN_SELF]: 'ไม่สามารถให้สิทธิ์ตัวเองได้',
};

const RoleUserAdditionSchema = z.object({
  users: z.array(z.string()),
});
type RoleUserAdditionFormInput = z.infer<typeof RoleUserAdditionSchema>;

interface RoleUserAdditionModalProps {
  open: boolean;
  onClose: () => void;
  appRoleId: string;
}

export default function RoleUserAdditionModal(props: Readonly<RoleUserAdditionModalProps>) {
  const { open: isOpen, onClose, appRoleId } = props;

  const { showSnackbar } = useSnackbar();

  const formContext = useForm<RoleUserAdditionFormInput>({
    defaultValues: {
      users: [],
    },
    resolver: zodResolver(RoleUserAdditionSchema),
  });
  const selectedUsers = formContext.watch('users');

  const [assignUserAppRole, { loading: isLoading }] = useAssignUserAppRoleMutation({
    onCompleted: (res) => {
      if (res?.suAssignUserAppRole.success) {
        showSnackbar({ message: 'Added Successfully', variant: 'success' });
        onClose();
      } else {
        let message = res.suAssignUserAppRole.message ?? 'Added Unsuccessfully';
        if (message.includes(AddUserToRoleErrorType.TRYING_TO_ASSIGN_SELF)) {
          message = ERROR_MESSAGE[AddUserToRoleErrorType.TRYING_TO_ASSIGN_SELF];
        }
        showSnackbar({ message: message, variant: 'error' });
      }
    },
    onError: (error) => {
      showSnackbar({
        message: error.message,
        variant: 'error',
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [SearchUserAppRoleViewDocument],
  });

  const handleAddUsersToRole = async (data: RoleUserAdditionFormInput) => {
    await assignUserAppRole({
      variables: {
        data: {
          userIds: data.users,
          appRoleId,
        },
      },
    });
  };

  const handleSelectRow = (selectedUsers: string[]) => {
    formContext.setValue('users', selectedUsers, { shouldDirty: true });
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth='lg'
        slotProps={{
          paper: { component: Form, onSubmit: formContext.handleSubmit(handleAddUsersToRole) },
          transition: {
            onExited: () => {
              formContext.reset();
            },
          },
        }}
      >
        <DialogTitle onClose={handleClose}>Add User</DialogTitle>
        <Divider />
        <DialogContent>
          <RoleUserAdditionList onSelectRow={handleSelectRow} appRoleId={appRoleId} />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Text>{selectedUsers.length} selected</Text>
          <Flex sx={(theme) => ({ gap: theme.spacing(3) })}>
            <Button size='large' variant='outlined' color='secondary' onClick={handleClose}>
              Cancel
            </Button>
            <FormButton label='Add User' size='large' loading={isLoading} />
          </Flex>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
